/**
 * Admin - Promo Codes Management
 * Create and manage promo codes
 */

import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React examples
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { adminAPI } from "utils/api";

// Property types
const PROPERTY_TYPES = ["villa", "apartment", "vacation home", "studio", "townhome", "house"];

function PromoCodesManagement() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    nameEn: "",
    descriptionEn: "",
    discountType: "percentage",
    discountValue: "",
    maxDiscountAmount: "",
    minimumAmount: "0",
    maxUses: "",
    maxUsesPerUser: "1",
    validFrom: "",
    validUntil: "",
    applicablePropertyTypes: [],
    applicableProperties: [],
    isActive: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [statusFilter, setStatusFilter] = useState("all");
  const [allPromoCodes, setAllPromoCodes] = useState([]); // Store all promo codes for analytics

  useEffect(() => {
    fetchPromoCodes();
    fetchProperties();
  }, [statusFilter]);

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (statusFilter !== "all") {
        filters.isActive = statusFilter === "active" ? "true" : "false";
      }
      const response = await adminAPI.getAllPromoCodes(filters);
      const codes = response.data || response.promoCodes || [];

      // If filtering, update filtered list; otherwise store all for analytics
      if (statusFilter === "all") {
        setAllPromoCodes(codes);
        setPromoCodes(codes);
      } else {
        setPromoCodes(codes);
        // Fetch all for analytics if we don't have them
        if (allPromoCodes.length === 0) {
          const allResponse = await adminAPI.getAllPromoCodes();
          setAllPromoCodes(allResponse.data || allResponse.promoCodes || []);
        }
      }
    } catch (error) {
      console.error("Error fetching promo codes:", error);
      setMessage({ type: "error", text: "Failed to load promo codes" });
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await adminAPI.getAllProperties();
      setProperties(response.properties || []);
    } catch (error) {
      console.error("Error fetching properties:", error);
    }
  };

  const handleOpenDialog = (promoCode = null) => {
    if (promoCode) {
      setEditingPromoCode(promoCode);
      setFormData({
        code: promoCode.code || "",
        nameEn: promoCode.name?.en || "",
        descriptionEn: promoCode.description?.en || "",
        discountType: promoCode.discountType || "percentage",
        discountValue: String(promoCode.discountValue || ""),
        maxDiscountAmount: promoCode.maxDiscountAmount ? String(promoCode.maxDiscountAmount) : "",
        minimumAmount: String(promoCode.minimumAmount || 0),
        maxUses: promoCode.maxUses ? String(promoCode.maxUses) : "",
        maxUsesPerUser: String(promoCode.maxUsesPerUser || 1),
        validFrom: promoCode.validFrom
          ? new Date(promoCode.validFrom).toISOString().split("T")[0]
          : "",
        validUntil: promoCode.validUntil
          ? new Date(promoCode.validUntil).toISOString().split("T")[0]
          : "",
        applicablePropertyTypes: promoCode.applicablePropertyTypes || [],
        applicableProperties: promoCode.applicableProperties || [],
        isActive: promoCode.isActive !== undefined ? promoCode.isActive : true,
      });
    } else {
      setEditingPromoCode(null);
      setFormData({
        code: "",
        nameEn: "",
        descriptionEn: "",
        discountType: "percentage",
        discountValue: "",
        maxDiscountAmount: "",
        minimumAmount: "0",
        maxUses: "",
        maxUsesPerUser: "1",
        validFrom: "",
        validUntil: "",
        applicablePropertyTypes: [],
        applicableProperties: [],
        isActive: true,
      });
    }
    setDialogOpen(true);
    setMessage({ type: "", text: "" });
  };

  const handleCloseDialog = () => {
    if (!submitting) {
      setDialogOpen(false);
      setEditingPromoCode(null);
      setFormData({
        code: "",
        nameEn: "",
        descriptionEn: "",
        discountType: "percentage",
        discountValue: "",
        maxDiscountAmount: "",
        minimumAmount: "0",
        maxUses: "",
        maxUsesPerUser: "1",
        validFrom: "",
        validUntil: "",
        applicablePropertyTypes: [],
        applicableProperties: [],
        isActive: true,
      });
      setMessage({ type: "", text: "" });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      // Validation
      if (!formData.code.trim()) {
        setMessage({ type: "error", text: "Promo code is required" });
        setSubmitting(false);
        return;
      }

      if (!formData.nameEn.trim()) {
        setMessage({ type: "error", text: "Promo code name is required" });
        setSubmitting(false);
        return;
      }

      if (!formData.discountValue || parseFloat(formData.discountValue) < 0) {
        setMessage({ type: "error", text: "Valid discount value is required" });
        setSubmitting(false);
        return;
      }

      if (formData.discountType === "percentage" && parseFloat(formData.discountValue) > 100) {
        setMessage({ type: "error", text: "Percentage discount cannot exceed 100%" });
        setSubmitting(false);
        return;
      }

      if (!formData.validFrom || !formData.validUntil) {
        setMessage({ type: "error", text: "Valid from and valid until dates are required" });
        setSubmitting(false);
        return;
      }

      const validFromDate = new Date(formData.validFrom);
      const validUntilDate = new Date(formData.validUntil);

      if (validUntilDate <= validFromDate) {
        setMessage({ type: "error", text: "Valid until date must be after valid from date" });
        setSubmitting(false);
        return;
      }

      const promoCodeData = {
        code: formData.code.trim().toUpperCase(),
        nameEn: formData.nameEn.trim(),
        nameAr: formData.nameEn.trim(), // Use English name for Arabic as well
        descriptionEn: formData.descriptionEn.trim(),
        descriptionAr: formData.descriptionEn.trim(), // Use English description for Arabic as well
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),
        maxDiscountAmount: formData.maxDiscountAmount
          ? parseFloat(formData.maxDiscountAmount)
          : undefined,
        minimumAmount: parseFloat(formData.minimumAmount) || 0,
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : undefined,
        maxUsesPerUser: parseInt(formData.maxUsesPerUser) || 1,
        validFrom: validFromDate.toISOString(),
        validUntil: validUntilDate.toISOString(),
        applicablePropertyTypes: formData.applicablePropertyTypes,
        applicableProperties: formData.applicableProperties.map((p) => p._id || p),
        isActive: formData.isActive,
      };

      if (editingPromoCode) {
        await adminAPI.updatePromoCode(editingPromoCode._id, promoCodeData);
        setMessage({ type: "success", text: "Promo code updated successfully" });
      } else {
        await adminAPI.createPromoCode(promoCodeData);
        setMessage({ type: "success", text: "Promo code created successfully" });
      }

      await fetchPromoCodes();
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);
    } catch (error) {
      console.error("Error saving promo code:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to save promo code. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (promoCodeId) => {
    if (!window.confirm("Are you sure you want to delete this promo code?")) {
      return;
    }

    try {
      await adminAPI.deletePromoCode(promoCodeId);
      setMessage({ type: "success", text: "Promo code deleted successfully" });
      await fetchPromoCodes();
      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 3000);
    } catch (error) {
      console.error("Error deleting promo code:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to delete promo code. Please try again.",
      });
      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 5000);
    }
  };

  const columns = [
    { Header: "Code", accessor: "code", width: "12%" },
    { Header: "Name (EN)", accessor: "nameEn", width: "15%" },
    { Header: "Discount", accessor: "discount", width: "12%" },
    { Header: "Valid Period", accessor: "validPeriod", width: "18%" },
    { Header: "Usage", accessor: "usage", width: "12%" },
    {
      Header: "Status",
      accessor: "isActive",
      width: "10%",
      Cell: ({ value }) => (
        <Chip
          label={value ? "Active" : "Inactive"}
          color={value ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      width: "21%",
      Cell: ({ row }) => {
        const promoCode = row.original;
        return (
          <MDBox display="flex" gap={1}>
            <IconButton size="small" onClick={() => handleOpenDialog(promoCode)} color="info">
              <Icon>edit</Icon>
            </IconButton>
            <IconButton size="small" onClick={() => handleDelete(promoCode._id)} color="error">
              <Icon>delete</Icon>
            </IconButton>
          </MDBox>
        );
      },
    },
  ];

  const rows = promoCodes.map((promoCode) => {
    const validFrom = new Date(promoCode.validFrom);
    const validUntil = new Date(promoCode.validUntil);
    const now = new Date();
    const isValid = promoCode.isActive && validFrom <= now && validUntil >= now;

    return {
      code: (
        <MDBox>
          <MDTypography variant="body2" fontWeight="bold">
            {promoCode.code}
          </MDTypography>
          {!isValid && <Chip label="Expired" color="error" size="small" sx={{ mt: 0.5 }} />}
        </MDBox>
      ),
      nameEn: promoCode.name?.en || "N/A",
      discount: (
        <MDBox>
          <MDTypography variant="body2" fontWeight="bold">
            {promoCode.discountType === "percentage"
              ? `${promoCode.discountValue}%`
              : `EGP ${promoCode.discountValue?.toLocaleString() || 0}`}
          </MDTypography>
          {promoCode.maxDiscountAmount && promoCode.discountType === "percentage" && (
            <MDTypography variant="caption" color="text">
              Max: EGP {promoCode.maxDiscountAmount.toLocaleString()}
            </MDTypography>
          )}
        </MDBox>
      ),
      validPeriod: (
        <MDBox>
          <MDTypography variant="caption" display="block">
            From: {validFrom.toLocaleDateString()}
          </MDTypography>
          <MDTypography variant="caption" display="block">
            Until: {validUntil.toLocaleDateString()}
          </MDTypography>
        </MDBox>
      ),
      usage: (
        <MDBox>
          <MDTypography variant="caption" display="block">
            Used: {promoCode.usedCount || 0}
          </MDTypography>
          {promoCode.maxUses && (
            <MDTypography variant="caption" display="block" color="text">
              Max: {promoCode.maxUses}
            </MDTypography>
          )}
        </MDBox>
      ),
      isActive: promoCode.isActive !== undefined ? promoCode.isActive : true,
      _id: promoCode._id,
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              Promo Codes Management
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Create and manage promotional codes for discounts
            </MDTypography>
          </MDBox>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => handleOpenDialog()}
            startIcon={<Icon>add</Icon>}
          >
            Create Promo Code
          </MDButton>
        </MDBox>

        {message.type && message.text && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        {/* Analytics Cards */}
        {(() => {
          const now = new Date();
          const analyticsData = allPromoCodes.length > 0 ? allPromoCodes : promoCodes;

          const totalCodes = analyticsData.length;
          const activeCodes = analyticsData.filter((pc) => pc.isActive).length;
          const inactiveCodes = analyticsData.filter((pc) => !pc.isActive).length;
          const totalUsage = analyticsData.reduce((sum, pc) => sum + (pc.usedCount || 0), 0);
          const expiredCodes = analyticsData.filter((pc) => {
            const validUntil = new Date(pc.validUntil);
            return validUntil < now;
          }).length;
          const validCodes = analyticsData.filter((pc) => {
            const validFrom = new Date(pc.validFrom);
            const validUntil = new Date(pc.validUntil);
            return pc.isActive && validFrom <= now && validUntil >= now;
          }).length;
          const codesAtLimit = analyticsData.filter((pc) => {
            return pc.maxUses && pc.usedCount >= pc.maxUses;
          }).length;

          return (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <MDBox p={3}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Total Promo Codes
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="bold">
                          {totalCodes}
                        </MDTypography>
                      </MDBox>
                      <MDBox
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          bgcolor: "info.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 12px rgba(33, 150, 243, 0.25)",
                        }}
                      >
                        <Icon sx={{ color: "white", fontSize: "2rem" }}>local_offer</Icon>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <MDBox p={3}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Active Codes
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="bold" color="success">
                          {activeCodes}
                        </MDTypography>
                      </MDBox>
                      <MDBox
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          bgcolor: "success.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.25)",
                        }}
                      >
                        <Icon sx={{ color: "white", fontSize: "2rem" }}>check_circle</Icon>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <MDBox p={3}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Total Usage
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="bold" color="primary">
                          {totalUsage.toLocaleString()}
                        </MDTypography>
                      </MDBox>
                      <MDBox
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          bgcolor: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 12px rgba(156, 39, 176, 0.25)",
                        }}
                      >
                        <Icon sx={{ color: "white", fontSize: "2rem" }}>trending_up</Icon>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <MDBox p={3}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Valid Codes
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="bold" color="success">
                          {validCodes}
                        </MDTypography>
                      </MDBox>
                      <MDBox
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          bgcolor: "success.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.25)",
                        }}
                      >
                        <Icon sx={{ color: "white", fontSize: "2rem" }}>verified</Icon>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <MDBox p={3}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Inactive Codes
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="bold" color="text">
                          {inactiveCodes}
                        </MDTypography>
                      </MDBox>
                      <MDBox
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          bgcolor: "grey.400",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 12px rgba(158, 158, 158, 0.25)",
                        }}
                      >
                        <Icon sx={{ color: "white", fontSize: "2rem" }}>cancel</Icon>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <MDBox p={3}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Expired Codes
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="bold" color="error">
                          {expiredCodes}
                        </MDTypography>
                      </MDBox>
                      <MDBox
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          bgcolor: "error.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 12px rgba(244, 67, 54, 0.25)",
                        }}
                      >
                        <Icon sx={{ color: "white", fontSize: "2rem" }}>schedule</Icon>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <MDBox p={3}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          At Usage Limit
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="bold" color="warning">
                          {codesAtLimit}
                        </MDTypography>
                      </MDBox>
                      <MDBox
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          bgcolor: "warning.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 12px rgba(255, 152, 0, 0.25)",
                        }}
                      >
                        <Icon sx={{ color: "white", fontSize: "2rem" }}>warning</Icon>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          );
        })()}

        {/* Filter and Table */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" fontWeight="bold">
              Promo Codes List
            </MDTypography>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minHeight: 44 }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </MDBox>
        </Card>

        <Card>
          {loading ? (
            <MDBox p={6} display="flex" justifyContent="center">
              <CircularProgress />
            </MDBox>
          ) : (
            <DataTable
              table={{ columns, rows }}
              showTotalEntries={true}
              isSorted={false}
              noEndBorder
              entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
              canSearch
            />
          )}
        </Card>

        {/* Create/Edit Promo Code Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingPromoCode ? "Edit Promo Code" : "Create New Promo Code"}
          </DialogTitle>
          <DialogContent>
            <MDBox display="flex" flexDirection="column" gap={3} pt={2}>
              {message.type && message.text && (
                <Alert severity={message.type}>{message.text}</Alert>
              )}

              <Grid container spacing={2}>
                {/* Promo Code */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Promo Code *"
                    fullWidth
                    value={formData.code}
                    onChange={(e) => handleInputChange("code", e.target.value.toUpperCase())}
                    required
                    helperText="Code will be converted to uppercase"
                    disabled={!!editingPromoCode}
                  />
                </Grid>

                {/* Name */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Name *"
                    fullWidth
                    value={formData.nameEn}
                    onChange={(e) => handleInputChange("nameEn", e.target.value)}
                    required
                  />
                </Grid>

                {/* Discount Type */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Discount Type *</InputLabel>
                    <Select
                      value={formData.discountType}
                      label="Discount Type *"
                      onChange={(e) => handleInputChange("discountType", e.target.value)}
                      sx={{ minHeight: 44 }}
                    >
                      <MenuItem value="percentage">Percentage</MenuItem>
                      <MenuItem value="fixed">Fixed Amount</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={formData.descriptionEn}
                    onChange={(e) => handleInputChange("descriptionEn", e.target.value)}
                  />
                </Grid>

                {/* Discount Value */}
                <Grid item xs={12} md={4}>
                  <TextField
                    label={
                      formData.discountType === "percentage"
                        ? "Discount (%) *"
                        : "Discount Amount (EGP) *"
                    }
                    type="number"
                    fullWidth
                    value={formData.discountValue}
                    onChange={(e) => handleInputChange("discountValue", e.target.value)}
                    required
                    inputProps={{
                      min: 0,
                      max: formData.discountType === "percentage" ? 100 : undefined,
                      step: 0.01,
                    }}
                  />
                </Grid>

                {/* Max Discount Amount (for percentage) */}
                {formData.discountType === "percentage" && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Max Discount Amount (EGP)"
                      type="number"
                      fullWidth
                      value={formData.maxDiscountAmount}
                      onChange={(e) => handleInputChange("maxDiscountAmount", e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Optional: Maximum discount amount"
                    />
                  </Grid>
                )}

                {/* Minimum Amount */}
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Minimum Booking Amount (EGP)"
                    type="number"
                    fullWidth
                    value={formData.minimumAmount}
                    onChange={(e) => handleInputChange("minimumAmount", e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Minimum amount required to use this code"
                  />
                </Grid>

                {/* Valid From */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Valid From *"
                    type="date"
                    fullWidth
                    value={formData.validFrom}
                    onChange={(e) => handleInputChange("validFrom", e.target.value)}
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Valid Until */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Valid Until *"
                    type="date"
                    fullWidth
                    value={formData.validUntil}
                    onChange={(e) => handleInputChange("validUntil", e.target.value)}
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Max Uses */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Max Uses (Total)"
                    type="number"
                    fullWidth
                    value={formData.maxUses}
                    onChange={(e) => handleInputChange("maxUses", e.target.value)}
                    inputProps={{ min: 0 }}
                    helperText="Leave empty for unlimited"
                  />
                </Grid>

                {/* Max Uses Per User */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Max Uses Per User *"
                    type="number"
                    fullWidth
                    value={formData.maxUsesPerUser}
                    onChange={(e) => handleInputChange("maxUsesPerUser", e.target.value)}
                    required
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                {/* Applicable Property Types */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Applicable Property Types</InputLabel>
                    <Select
                      multiple
                      value={formData.applicablePropertyTypes}
                      label="Applicable Property Types"
                      onChange={(e) => handleInputChange("applicablePropertyTypes", e.target.value)}
                      renderValue={(selected) => selected.join(", ") || "All Property Types"}
                      sx={{ minHeight: 44 }}
                    >
                      {PROPERTY_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          <Checkbox checked={formData.applicablePropertyTypes.indexOf(type) > -1} />
                          <ListItemText primary={type} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Applicable Properties */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={properties}
                    getOptionLabel={(option) =>
                      `${option.title?.en || option.title || "Unknown"} ${
                        option.shortId ? `(${option.shortId})` : ""
                      }`
                    }
                    value={formData.applicableProperties}
                    onChange={(event, newValue) => {
                      handleInputChange("applicableProperties", newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Applicable Properties"
                        placeholder="Select specific properties (leave empty for all)"
                        helperText="Leave empty to apply to all properties"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option._id || index}
                          label={`${option.title?.en || option.title || "Unknown"} ${
                            option.shortId ? `(${option.shortId})` : ""
                          }`}
                          size="small"
                        />
                      ))
                    }
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    fullWidth
                  />
                </Grid>

                {/* Active Status */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => handleInputChange("isActive", e.target.checked)}
                        color={formData.isActive ? "success" : "default"}
                      />
                    }
                    label="Promo Code is Active"
                  />
                </Grid>
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCloseDialog} disabled={submitting}>
              Cancel
            </MDButton>
            <MDButton
              onClick={handleSave}
              variant="gradient"
              color="info"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <Icon>save</Icon>}
            >
              {submitting ? "Saving..." : editingPromoCode ? "Update" : "Create"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PromoCodesManagement;
